import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { vlansAPI, subnetsAPI, Subnet } from '../services/api';
import { Vlan, VlanStatus } from '../types';

const statusOptions: { label: string; value: VlanStatus }[] = [
  { label: 'Aktivan', value: 'active' },
  { label: 'Neaktivan', value: 'inactive' }
];

const statusSeverity: Record<VlanStatus, 'success' | 'secondary'> = {
  active: 'success',
  inactive: 'secondary'
};

const ITVlanManagement: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [vlans, setVlans] = useState<Vlan[]>([]);
  const [subnets, setSubnets] = useState<Subnet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [nameFilter, setNameFilter] = useState('');
  const [vlanIdFilter, setVlanIdFilter] = useState('');
  const [subnetFilter, setSubnetFilter] = useState('');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<Vlan | null>(null);
  const [form, setForm] = useState<{
    name: string;
    vlanId: number | null;
    subnetId: string | null;
    status: VlanStatus;
    description: string;
  }>({
    name: '',
    vlanId: null,
    subnetId: null,
    status: 'active',
    description: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [vlanData, subnetData] = await Promise.all([vlansAPI.getAll(), subnetsAPI.getAll()]);
      setVlans(vlanData);
      setSubnets(subnetData);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju VLAN-ova');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      vlanId: null,
      subnetId: null,
      status: 'active',
      description: ''
    });
    setEditVisible(true);
  };

  const openEdit = (row: Vlan) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      vlanId: row.vlanId || null,
      subnetId: (typeof row.subnetId === 'string' ? row.subnetId : row.subnetId?._id) || null,
      status: row.status || 'active',
      description: row.description || ''
    });
    setEditVisible(true);
  };

  const confirmDelete = (row: Vlan) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati VLAN "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await vlansAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'VLAN obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju VLAN-a',
            life: 5000
          });
        }
      }
    });
  };

  const canSubmit = useMemo(() => {
    return form.name.trim() && form.vlanId !== null && form.vlanId >= 1 && form.vlanId <= 4094;
  }, [form.name, form.vlanId]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Naziv i ispravan VLAN ID (1-4094) su obavezni',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        vlanId: form.vlanId!,
        description: form.description.trim() || undefined,
        subnetId: form.subnetId || undefined,
        status: form.status
      };
      if (editing) {
        await vlansAPI.update(editing._id, payload);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'VLAN ažuriran', life: 2500 });
      } else {
        await vlansAPI.create(payload);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'VLAN kreiran', life: 2500 });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju VLAN-a',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBody = (row: Vlan) => (
    <Tag value={row.status === 'active' ? 'Aktivan' : 'Neaktivan'} severity={statusSeverity[row.status]} />
  );

  const subnetBody = (row: Vlan) => {
    const subnetName =
      typeof row.subnetId === 'string'
        ? row.subnetName
        : row.subnetId
        ? `${row.subnetId.name} (${row.subnetId.cidr})`
        : row.subnetName;
    return subnetName || '-';
  };

  const filteredVlans = vlans.filter((v) => {
    const nameMatch = !nameFilter || v.name.toLowerCase().includes(nameFilter.toLowerCase());
    const vlanIdMatch =
      !vlanIdFilter || String(v.vlanId).toLowerCase().includes(vlanIdFilter.toLowerCase());
    const subnetName =
      typeof v.subnetId === 'string'
        ? v.subnetName || ''
        : v.subnetId
        ? `${v.subnetId.name} ${v.subnetId.cidr}`
        : v.subnetName || '';
    const subnetMatch =
      !subnetFilter || subnetName.toLowerCase().includes(subnetFilter.toLowerCase());
    return nameMatch && vlanIdMatch && subnetMatch;
  });

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h2 className="m-0">VLAN Upravljanje</h2>
          <Button label="Dodaj VLAN" icon="pi pi-plus" onClick={openNew} />
        </div>

        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <InputText
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Naziv"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-4">
            <InputText
              value={vlanIdFilter}
              onChange={(e) => setVlanIdFilter(e.target.value)}
              placeholder="VLAN ID"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-4">
            <InputText
              value={subnetFilter}
              onChange={(e) => setSubnetFilter(e.target.value)}
              placeholder="Subnet"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <DataTable
          value={filteredVlans}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema VLAN-ova"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="vlanId" header="VLAN ID" sortable />
          <Column field="description" header="Opis" sortable />
          <Column header="Subnet" body={subnetBody} sortable />
          <Column field="status" header="Status" body={statusBody} sortable />
          <Column
            header="Akcije"
            body={(row: Vlan) => (
              <div className="flex gap-2">
                <Button
                  icon="pi pi-pencil"
                  className="p-button-sm p-button-rounded p-button-text"
                  onClick={() => openEdit(row)}
                  tooltip="Uredi"
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-sm p-button-rounded p-button-text p-button-danger"
                  onClick={() => confirmDelete(row)}
                  tooltip="Obriši"
                />
              </div>
            )}
            style={{ width: '160px' }}
          />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni VLAN' : 'Dodaj novi VLAN'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '600px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setEditVisible(false)}
            />
            <Button
              label="Sačuvaj"
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Naziv VLAN-a *</label>
            <InputText
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Unesite naziv VLAN-a"
            />
          </div>
          <div className="field col-12">
            <label>VLAN ID *</label>
            <InputText
              value={form.vlanId !== null ? String(form.vlanId) : ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  vlanId: e.target.value ? Number(e.target.value) : null
                }))
              }
              placeholder="Unesite VLAN ID (1-4094)"
            />
          </div>
          <div className="field col-12">
            <label>Subnet</label>
            <Dropdown
              value={form.subnetId}
              options={subnets}
              optionLabel="name"
              optionValue="_id"
              onChange={(e) => setForm((p) => ({ ...p, subnetId: e.value }))}
              placeholder="Izaberite subnet"
              filter
              showClear
              appendTo="self"
            />
          </div>
          <div className="field col-12">
            <label>Status</label>
            <Dropdown
              value={form.status}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, status: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              autoResize
              placeholder="Unesite opis VLAN-a"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITVlanManagement;






